import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound, Webhook, Copy, RefreshCw, Play, Loader2,
  ShieldCheck, MessageCircle, Power, CheckCircle2, XCircle,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Btn, Input, Badge } from "@/components/kit";

const WH = process.env.REACT_APP_BACKEND_URL;

const copy = (txt, label = "Copiado") => {
  try {
    const p = navigator.clipboard?.writeText(txt);
    if (p && typeof p.then === "function") {
      p.then(() => toast.success(label)).catch(() => toast.error("Copia bloqueada pelo navegador"));
      return;
    }
  } catch (_) { /* fall through to fallback */ }
  // Fallback for insecure contexts / permission denied
  try {
    const ta = document.createElement("textarea");
    ta.value = txt; ta.style.position = "fixed"; ta.style.left = "-9999px";
    document.body.appendChild(ta); ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    toast.success(label);
  } catch (_) {
    toast.error("Copia bloqueada pelo navegador");
  }
};

function Mono({ children, className = "" }) {
  return <code className={`rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-xs ${className}`}>{children}</code>;
}

export default function Integrations() {
  return (
    <div className="space-y-5" data-testid="integrations-page">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600"><MessageCircle size={20} /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">WhatsApp (Z-API)</h1>
          <p className="text-sm text-muted-foreground">Conecte a ISA ao WhatsApp real da sua equipe. Outras integrações chegam em breve.</p>
        </div>
      </div>

      <WhatsAppSection />
    </div>
  );
}

/* ------------------------------ WhatsApp (Z-API) ------------------------------ */
function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
      {ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {label}
    </span>
  );
}

function WhatsAppSection() {
  const [health, setHealth] = useState(null);
  const [config, setConfig] = useState(null);
  const [creds, setCreds] = useState({});
  const [webhookToken, setWebhookToken] = useState("");
  const [form, setForm] = useState({ zapi_instance_id: "", zapi_token: "", zapi_client_token: "", zapi_webhook_token: "" });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const [h, c] = await Promise.all([
        api.get("/admin/zapi/health"),
        api.get("/admin/zapi/config"),
      ]);
      setHealth(h.data); setConfig(c.data); setCreds(c.data?.credentials || {});
      try { const { data } = await api.get("/admin/zapi/webhook-url"); setWebhookToken(data?.webhook_token || ""); } catch { /* non-admin */ }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const active = config?.outbound_provider === "zapi";
  const credsOk = health?.instance_configured && health?.token_configured && health?.client_token_configured;

  // Send only fields the operator actually typed (blank = keep what's stored).
  const changedPayload = (extra = {}) => {
    const p = { ...extra };
    for (const k of ["zapi_instance_id", "zapi_token", "zapi_client_token", "zapi_webhook_token"]) {
      if (form[k].trim()) p[k] = form[k].trim();
    }
    return p;
  };

  const saveCreds = async () => {
    const payload = changedPayload();
    if (Object.keys(payload).length === 0) return toast.error("Preencha ao menos um campo para salvar");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/zapi/config", payload);
      setCreds(data.credentials || {});
      setForm({ zapi_instance_id: "", zapi_token: "", zapi_client_token: "", zapi_webhook_token: "" });
      toast.success("Credenciais salvas com segurança");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/zapi/config", changedPayload({ outbound_provider: "zapi" }));
      setCreds(data.credentials || {});
      toast.success("WhatsApp (Z-API) ativado para sua empresa");
      setForm({ zapi_instance_id: "", zapi_token: "", zapi_client_token: "", zapi_webhook_token: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const useSimulator = async () => {
    setBusy(true);
    try {
      await api.post("/admin/zapi/config", { outbound_provider: "simulator" });
      toast.success("Voltou para o Simulador");
      setConfig((c) => ({ ...c, outbound_provider: "simulator" }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    const digits = testPhone.replace(/\D/g, "");
    if (digits.length < 10) return toast.error("Informe o número com DDI+DDD (ex: 5511999998888)");
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/zapi/test-message", { phone: digits });
      setTestResult(data);
      if (data.ok) toast.success("Mensagem de teste enviada! Confira o WhatsApp.");
      else toast.error(`Falhou: ${data.error || data.status}`);
    } catch (e) { setTestResult({ ok: false, error: apiErr(e) }); toast.error(apiErr(e)); }
    finally { setTesting(false); }
  };

  const webhookIn = `${WH}/api/webhooks/zapi`;
  const webhookStatus = `${WH}/api/webhooks/zapi/status`;
  const tokenForUrl = (form.zapi_webhook_token.trim() || webhookToken || "").trim();
  const fullUrl = (base) => tokenForUrl ? `${base}?token=${encodeURIComponent(tokenForUrl)}` : base;

  if (loading) return <Card className="flex items-center justify-center p-12"><Loader2 className="animate-spin text-muted-foreground" /></Card>;

  const CRED_FIELDS = [
    { key: "zapi_instance_id", cred: "instance_id", label: "ID da instância", hint: "Ex: 3F2A80199332F14A93BCD217A5857A3E", help: "Painel Z-API → sua instância → ID." },
    { key: "zapi_token", cred: "token", label: "Token da instância", hint: "Token da instância (Instance Token)", help: "Painel Z-API → sua instância → Token." },
    { key: "zapi_client_token", cred: "client_token", label: "Client-Token (conta)", hint: "Client-Token da conta", help: "Painel Z-API → Segurança → Account Security Token." },
    { key: "zapi_webhook_token", cred: "webhook_token", label: "Token do webhook", hint: "Um segredo que você escolhe", help: "Um valor secreto seu; usado no ?token= das URLs abaixo." },
  ];

  return (
    <div className="space-y-5" data-testid="whatsapp-section">
      {/* status */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600"><MessageCircle size={20} /></div>
            <div>
              <h2 className="font-display text-lg font-semibold">WhatsApp via Z-API</h2>
              <p className="text-sm text-muted-foreground">Conecte a ISA ao WhatsApp real da sua equipe — sem redeploy.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={active ? "pos" : "muted"} data-testid="zapi-provider-badge">{active ? "Z-API ativo" : "Usando Simulador"}</Badge>
            <Btn variant="ghost" onClick={load} data-testid="zapi-refresh"><RefreshCw size={15} /> Atualizar</Btn>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="zapi-health">
          <StatusPill ok={health?.instance_configured} label="ID da instância" />
          <StatusPill ok={health?.token_configured} label="Token da instância" />
          <StatusPill ok={health?.client_token_configured} label="Client-Token" />
          <StatusPill ok={health?.webhook_token_configured} label="Token de webhook" />
          <StatusPill ok={health?.connected === true} label={health?.connected === true ? "Instância conectada" : "Instância desconectada"} />
        </div>
      </Card>

      {/* step 1 — credentials */}
      <Card className="p-5">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold"><KeyRound size={17} className="text-primary" /> 1 · Credenciais da Z-API</h3>
        <p className="mt-1 text-sm text-muted-foreground">Cole os dados do painel da Z-API. Ficam guardados <b>criptografados</b> e vinculados só à sua empresa. Deixe em branco para manter o valor atual.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="zapi-cred-form">
          {CRED_FIELDS.map((f) => {
            const c = creds[f.cred] || {};
            return (
              <div key={f.key}>
                <label className="flex items-center justify-between text-xs font-medium text-foreground">
                  <span>{f.label}</span>
                  {c.configured
                    ? <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`cred-status-${f.cred}`}><CheckCircle2 size={12} /> configurado</span>
                    : <span className="text-amber-600" data-testid={`cred-status-${f.cred}`}>pendente</span>}
                </label>
                <Input data-testid={`zapi-input-${f.cred}`}
                  placeholder={c.configured ? `Salvo: ${c.masked} — cole para substituir` : f.hint}
                  value={form[f.key]} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="mt-1" />
                <p className="mt-1 text-[11px] text-muted-foreground">{f.help}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <Btn onClick={saveCreds} disabled={saving} data-testid="zapi-save-creds">{saving ? <Loader2 className="animate-spin" size={15} /> : <ShieldCheck size={15} />} Salvar credenciais</Btn>
        </div>
      </Card>

      {/* step 2 — webhooks */}
      <Card className="p-5">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Webhook size={17} className="text-primary" /> 2 · Configurar webhooks na Z-API</h3>
        <p className="mt-1 text-sm text-muted-foreground">Copie a <b>URL completa</b> abaixo (já vem com o seu token) e cole no painel da Z-API, em <b>"Webhooks e configurações gerais"</b>. Cole exatamente como está — não troque nada.</p>
        {!tokenForUrl && (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="wh-no-token">
            Defina e salve o <b>Token do webhook</b> no passo 1 primeiro — sem ele a URL fica incompleta e o WhatsApp não conecta.
          </p>
        )}
        <div className="mt-3 space-y-3">
          {[
            { label: "Ao receber mensagem (Ao enviar/receber)", url: webhookIn, tid: "wh-in" },
            { label: "Ao atualizar status da mensagem", url: webhookStatus, tid: "wh-status" },
          ].map((w) => (
            <div key={w.tid}>
              <p className="mb-1 text-xs font-medium text-muted-foreground">{w.label}</p>
              <div className="flex items-center gap-2">
                <Mono className="flex-1 overflow-auto whitespace-pre px-3 py-2" data-testid={`${w.tid}-url`}>{fullUrl(w.url)}</Mono>
                <button onClick={() => copy(fullUrl(w.url), "URL completa copiada")} disabled={!tokenForUrl}
                  className="text-muted-foreground hover:text-primary disabled:opacity-40" data-testid={`${w.tid}-copy`}><Copy size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">Dica: no painel da Z-API essas opções ficam como "Ao enviar" / "Ao receber" e "Ao atualizar status da mensagem".</p>
      </Card>

      {/* step 3 — connect + activate */}
      <Card className="p-5">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Power size={17} className="text-primary" /> 3 · Conectar e ativar</h3>
        <p className="mt-1 text-sm text-muted-foreground">No painel da Z-API, conecte a instância pelo <b>QR Code</b> (precisa aparecer "Instância conectada" acima). Depois ative o envio real — só a sua empresa é afetada.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {active
            ? <Btn variant="ghost" onClick={useSimulator} disabled={busy} data-testid="zapi-use-simulator"><Power size={15} /> Voltar ao simulador</Btn>
            : <Btn onClick={activate} disabled={busy || !credsOk} data-testid="zapi-activate">{busy ? <Loader2 className="animate-spin" size={15} /> : <Power size={15} />} Ativar WhatsApp</Btn>}
          {!active && !credsOk && <span className="text-xs text-amber-600">Salve as credenciais obrigatórias (instância, token e client-token) antes de ativar.</span>}
        </div>
        <ol className="mt-4 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
          <li>Preencha e salve as credenciais no passo 1.</li>
          <li>Cole as URLs do passo 2 na Z-API (com o token do webhook).</li>
          <li>Conecte a instância pelo QR Code no painel da Z-API.</li>
          <li>Clique em <b>Ativar WhatsApp</b> aqui.</li>
          <li>Cadastre os funcionários com o WhatsApp deles em <b>Pessoas &amp; Permissões</b> — só números autorizados conversam com a ISA.</li>
        </ol>
      </Card>

      {/* test connection */}
      <Card className="p-5" data-testid="zapi-test-card">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Play size={17} className="text-primary" /> Testar conexão</h3>
        <p className="mt-1 text-sm text-muted-foreground">Envie uma mensagem de teste agora, direto pela Z-API, e veja o resultado na hora. Não depende do restante da configuração de fila.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input data-testid="zapi-test-phone" placeholder="Número com DDI+DDD (ex: 5511999998888)"
            value={testPhone} onChange={(e) => setTestPhone(e.target.value)} className="min-w-[280px] flex-1" />
          <Btn onClick={sendTest} disabled={testing || !credsOk} data-testid="zapi-send-test">
            {testing ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />} Enviar teste
          </Btn>
        </div>
        {!credsOk && <p className="mt-2 text-xs text-amber-600">Salve as credenciais (instância, token e client-token) antes de testar.</p>}
        {testResult && (
          <div className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${testResult.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="zapi-test-result">
            {testResult.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
            <span>{testResult.ok
              ? <>Enviado com sucesso (status: <b>{testResult.status}</b>). Confira o WhatsApp do número informado.</>
              : <>Não foi possível enviar{testResult.status ? <> (status: <b>{testResult.status}</b>)</> : ""}: <b>{testResult.error || "erro desconhecido"}</b>. Verifique se a instância está conectada (QR Code) e as credenciais.</>}
            </span>
          </div>
        )}
      </Card>
    </div>
  );
}
